import { useCallback, useMemo, useState } from 'react';
import { AppointmentData } from 'pages/Conversations/components/ChannelView/Header/ConversationDetailsModal/types/AppointmentData';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useAppSelector } from 'state/hooks';
import {
  AppointmentCreateInput,
  AppointmentStatus,
  AppointmentUpdateInput,
  GetPaginatedAppointmentsDocument,
  useCreateOneAppointmentMutation,
  useTriggerAiRecapExportToPimsMutation,
  useUpdateOneAppointmentMutation,
} from 'shared/types/graphql';
import * as Sentry from '@sentry/react';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider/context/IntegrationsProvider';
import { ModuleName, useModularity } from 'shared/hooks/useModularity';
import useSaveNonStandardClinicAppointment from './useSaveNonStandardClinicAppointment';

interface SaveAppointmentDraftReturn {
  saveAppointmentDraft: (updates?: Partial<AppointmentData>) => Promise<void>;
  cancelAppointment: (id: string) => Promise<void>;
  isSavingChanges: boolean;
  isDeletingAppointment: boolean;
}

type CrudMethod = 'create' | 'update' | 'delete';
export const useSaveAppointmentDraft = (): SaveAppointmentDraftReturn => {
  const toast = useToast();
  const { currentClinicId, currentClinic } = useClinicUser();
  const { hasModule } = useModularity();
  const isStandardClinic = hasModule(ModuleName.AppointmentsStandard);
  const {
    createAppointment: createRecapClinicAppointment,
    updateAppointment: updateRecapClinicAppointment,
    isLoading: isLoadingRecapClinicOperations,
  } = useSaveNonStandardClinicAppointment();
  const { isExportToPimsSupported, primaryIntegrationName } = useIntegrationsProvider();
  const isAiRecapWritebackEnabled = isExportToPimsSupported && currentClinic?.clinicSetting?.aiRecapWritebackEnabled;

  const { appointmentDraft, appointmentId, originalAppointment, surveyAutomationIds } = useAppSelector(
    (state) => state.appointmentsModal,
  );
  const [crudMethod, setCrudMethod] = useState<CrudMethod>('create');

  const [createAppointmentMutation, { loading: isCreatingAppointment }] = useCreateOneAppointmentMutation({
    refetchQueries: [GetPaginatedAppointmentsDocument],
  });

  const [updateAppointmentMutation, { loading: isUpdatingAppointment }] = useUpdateOneAppointmentMutation({
    refetchQueries: [GetPaginatedAppointmentsDocument],
  });

  const [triggerAiRecapExportToPims, { loading: isExportingAiRecapToPims }] = useTriggerAiRecapExportToPimsMutation({
    refetchQueries: [GetPaginatedAppointmentsDocument],
  });

  const isLoading = isCreatingAppointment || isUpdatingAppointment || isExportingAiRecapToPims;

  const cancelAppointment = useCallback(
    async (id: string) => {
      setCrudMethod('delete');
      try {
        await updateAppointmentMutation({
          variables: {
            where: { id },
            data: {
              status: AppointmentStatus.Cancelled,
              isDeleted: true,
            },
          },
        });
      } catch (e) {
        Sentry.captureException(e);
        toast({
          status: 'error',
          description: 'Oops, an error occurred while cancelling your appointment.',
        });
        console.error(e);
      }
    },
    [toast, updateAppointmentMutation],
  );

  const updatedAutomationsList = useMemo(() => {
    const { selectedAutomationIds } = appointmentDraft;
    let _selectedAutomationIds: string[] = [];

    // Set default value to any automations originally attached to the appointment
    if (selectedAutomationIds) {
      _selectedAutomationIds = selectedAutomationIds;
    }

    // Add survey automations back to the list to be filtered out by the back end if necessary based on survey settings
    if (surveyAutomationIds) {
      _selectedAutomationIds = [..._selectedAutomationIds, ...surveyAutomationIds];
    }

    return _selectedAutomationIds.map((id) => ({ id }));
  }, [appointmentDraft, surveyAutomationIds]);

  const createAppointment = useCallback(async () => {
    const {
      clinicEmployeeId,
      roomId,
      appointmentTypeId,
      startAt,
      description,
      petId,
      durationInMinutes,
      petParentIds,
    } = appointmentDraft;

    const data: AppointmentCreateInput = {
      description: description || '',
      clinic: { connect: { id: currentClinicId } },
      startAt,
      durationInMinutes,
      workflows: {
        connect: updatedAutomationsList,
      },
      clinicPet: { connect: { id: petId } },
      clinicPetParents: {
        connect: petParentIds?.map((id) => ({ id })),
      },
      ...(roomId && { room: { connect: { id: roomId } } }),
      ...(appointmentTypeId && { appointmentType: { connect: { id: appointmentTypeId } } }),
      ...(clinicEmployeeId && { clinicEmployees: { connect: [{ id: clinicEmployeeId }] } }),
    };

    try {
      if (isStandardClinic) {
        await createAppointmentMutation({
          variables: {
            data,
          },
        });
      } else {
        await createRecapClinicAppointment(appointmentDraft);
      }
      toast({ title: 'Success!', description: 'Your appointment has been successfully created', status: 'success' });
    } catch (e) {
      Sentry.captureException(e);
      toast({
        status: 'error',
        title: 'Sorry!',
        description: 'An error occurred while creating your appointment.',
      });
      console.error(e);
    }
  }, [
    appointmentDraft,
    currentClinicId,
    updatedAutomationsList,
    isStandardClinic,
    toast,
    createRecapClinicAppointment,
    createAppointmentMutation,
  ]);

  const updateAppointment = useCallback(async () => {
    if (!appointmentId) return;

    const {
      clinicEmployeeId,
      roomId,
      appointmentTypeId,
      startAt,
      description,
      recapNotes,
      petId,
      durationInMinutes,
      petParentIds,
    } = appointmentDraft;

    const data: AppointmentUpdateInput = {
      description,
      recapNotes,
      clinic: { connect: { id: currentClinicId } },
      startAt,
      durationInMinutes,
      clinicPet: { connect: { id: petId } },
      room: roomId ? { connect: { id: roomId } } : { disconnect: true },
      appointmentType: appointmentTypeId ? { connect: { id: appointmentTypeId } } : { disconnect: true },
      clinicEmployees: {
        ...(clinicEmployeeId && { set: [{ id: clinicEmployeeId }] }),
        ...(!clinicEmployeeId &&
          originalAppointment?.clinicEmployeeId && {
          disconnect: [{ id: originalAppointment?.clinicEmployeeId }],
        }),
      },
      workflows: {
        set: updatedAutomationsList,
      },
      clinicPetParents: {
        connect: petParentIds?.map((id) => ({ id })),
        ...(originalAppointment?.petParentIds && {
          disconnect: originalAppointment?.petParentIds.map((petParentId) => {
            return { id: petParentId };
          }),
        }),
      },
    };

    const shouldExportToPims =
      isAiRecapWritebackEnabled &&
      !!appointmentDraft?.aiRecapWriteback &&
      appointmentDraft.recapNotes !== originalAppointment?.recapNotes;

    try {
      if (isStandardClinic) {
        await updateAppointmentMutation({
          variables: {
            where: {
              id: appointmentId,
            },
            data,
          },
        });
      } else {
        await updateRecapClinicAppointment(appointmentId, appointmentDraft, originalAppointment);
      }
      toast({
        title: 'Success!',
        description: `Your appointment has been successfully ${crudMethod === 'delete' ? 'deleted' : 'updated'}`,
        status: 'success',
      });
      if (shouldExportToPims) {
        const response = await triggerAiRecapExportToPims({
          variables: {
            where: {
              id: appointmentId,
            },
          },
        });
        const { success } = response.data?.aiRecapExportToPims || {};
        toast({
          title: success ? 'Sync Successful' : 'Sync Failed',
          description: success
            ? `AI Recap successfully written back to ${primaryIntegrationName}`
            : `AI Recap failed to write back to ${primaryIntegrationName}`,
          status: success ? 'success' : 'error',
        });
      }
    } catch (e) {
      Sentry.captureException(e);
      toast({
        status: 'error',
        title: 'Sorry!',
        description: `An error occurred while ${crudMethod === 'delete' ? 'deleting' : 'updating'} your appointment`,
      });
      console.error(e);
    }
  }, [
    appointmentDraft,
    appointmentId,
    crudMethod,
    currentClinicId,
    isAiRecapWritebackEnabled,
    isStandardClinic,
    originalAppointment,
    primaryIntegrationName,
    toast,
    triggerAiRecapExportToPims,
    updateAppointmentMutation,
    updateRecapClinicAppointment,
    updatedAutomationsList,
  ]);

  const saveAppointmentDraft = useCallback(async () => {
    if (appointmentId) {
      setCrudMethod('update');
      await updateAppointment();
    } else {
      setCrudMethod('create');
      await createAppointment();
    }
  }, [appointmentId, createAppointment, updateAppointment]);

  return {
    saveAppointmentDraft,
    cancelAppointment,
    isSavingChanges: (isLoading || isLoadingRecapClinicOperations) && crudMethod !== 'delete',
    isDeletingAppointment: isUpdatingAppointment && crudMethod === 'delete',
  };
};
